
import Vue, { PropType } from 'vue';
import {
  Assessment,
  AssessmentResult,
  ASSESSMENT_STATUS,
} from '@/models/greathall/assessment.model';
import LineSkeleton from '@/components/skeletons/atoms/LineSkeleton.vue';
import { Icon, Modal } from '@descomplica/vue-components';
import VueCookie from 'vue-cookie';
import PostGradeAssessmentCardSkeleton from '@/components/skeletons/atoms/PostGradeAssessmentCardSkeleton.vue';

interface Data {
  isActive: boolean;
  showLoading: boolean;
  showError: boolean;
  isResetingAssignment: boolean;
}

export default Vue.extend({
  components: {
    Icon,
    Modal,
    LineSkeleton,
    PostGradeAssessmentCardSkeleton,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    assessment: {
      type: Object as PropType<Assessment>,
      required: true,
    },
    lastAssessmentResult: {
      type: Object as PropType<AssessmentResult>,
      default: null,
    },
    assessmentStatus: {
      type: String as PropType<ASSESSMENT_STATUS>,
      default: null,
    },
  },
  data(): Data {
    return {
      isActive: this.active,
      showLoading: true,
      showError: false,
      isResetingAssignment: false,
    };
  },
  computed: {
    borderRadius(): number {
      return this.getBorderRadius(window.innerWidth);
    },
    numberOfQuestions(): number {
      return this.assessment?.dexExamGroup?.totalQuestions;
    },
    minRightAnswers(): number {
      const min = this.numberOfQuestions * this.assessment.passingGradePercentage;
      return Math.ceil(min);
    },
    maxDuration(): string {
      const totalDuration = this.assessment?.dexExamGroup?.totalDuration || '-';
      return `${totalDuration}min`;
    },
    passingPercentage(): number {
      const passingPercentage = this.assessment?.passingGradePercentage || 0;
      return passingPercentage * 100;
    },
  },
  watch: {
    active(value) {
      this.isActive = value;
    },
    loading: {
      immediate: true,
      handler(value) {
        this.showLoading = value;
      },
    },
  },
  methods: {
    getBorderRadius(width: number): number {
      if (width > 495) return 24;
      return 0;
    },
    updateActive(active: boolean) {
      this.$emit('update:active', active);
    },
    onCloseClick() {
      this.updateActive(false);
    },
    async onGoToAssessmentClick(): Promise<void> {
      if (this.isResetingAssignment) return;

      this.showError = false;

      if (this.assessmentStatus === ASSESSMENT_STATUS.RETRY) await this.resetAssignment();

      this.setAssessmentCookies();

      window.open(this.assessment.currentVariantByUser.link, '_self');
    },
    async resetAssignment(): Promise<void> {
      this.isResetingAssignment = true;
      try {
        await this.$store.dispatch('RESET_DEX_ASSESSMENT', {
          assessmentResultId: this.lastAssessmentResult.id,
          dexCourseId: this.$store.getters.currentCourse.id,
        });
      } catch (error) {
        this.showError = true;
        throw error;
      } finally {
        this.isResetingAssignment = false;
      }
    },
    setAssessmentCookies(): void {
      const { currentUser, currentCourse } = this.$store.getters;

      const greathallAssessment = JSON.stringify({
        dexUserId: currentUser.id,
        dexCourseId: currentCourse.id,
        dexCourseName: currentCourse.name,
        assessmentId: this.assessment.id,
        assessmentCategory: this.assessment.category?.name,
        variantId: this.assessment.currentVariantByUser.id,
        dexExamGroupId: this.assessment.currentVariantByUser.dexExamGroupId,
      });

      const cookieOptions = { domain: '.descomplica.com.br', expires: '7D' };
      VueCookie.set('greathallAssessment', greathallAssessment, cookieOptions);

      if (this.assessmentStatus === ASSESSMENT_STATUS.WAITING_GRADING)
        VueCookie.set('dexExamsAssessmentResultId', this.lastAssessmentResult.id, cookieOptions);
    },
  },
});
